/**
 * Quarter-hour picker for <input type="time" step="900">.
 * Native pickers list every minute whatever the step, so their popup is replaced
 * by the same two columns (hours / minutes) with minutes 00, 15, 30 and 45,
 * from 09:00 to 21:00. The field itself stays a native time input: typing works
 * and is snapped to the nearest quarter inside that range on change.
 */
const QuarterTimePicker = {
    SELECTOR: 'input[type="time"][step="900"]',
    MINUTES: ['00', '15', '30', '45'],
    FIRST_HOUR: 9,
    LAST_HOUR: 21, // 21:00 is the last selectable time
    panel: null,
    input: null,

    init() {
        document.addEventListener('click', event => {
            const input = event.target.closest?.(this.SELECTOR);
            if (input && !input.disabled && !input.readOnly) {
                event.preventDefault();
                this.panel && this.input === input ? this.close() : this.open(input);
            } else if (this.panel && !this.panel.contains(event.target)) {
                this.close();
            }
        }, true);
        document.addEventListener('keydown', event => {
            if (event.key === 'Escape' && this.panel) { this.close(); return; }
            const input = event.target.closest?.(this.SELECTOR);
            // Alt+↓ and F4 would open the native minute list.
            if (input && ((event.altKey && event.key === 'ArrowDown') || event.key === 'F4')) {
                event.preventDefault();
                this.open(input);
            }
        }, true);
        document.addEventListener('change', event => {
            if (event.target.matches?.(this.SELECTOR)) this.snap(event.target);
        }, true);
        document.addEventListener('scroll', event => {
            if (this.panel && !this.panel.contains(event.target)) this.close();
        }, true);
        window.addEventListener('resize', () => this.close());
    },

    pad(n) { return String(n).padStart(2, '0'); },

    snap(input) {
        const match = /^(\d{2}):(\d{2})/.exec(input.value);
        if (!match) return;
        let hour = Number(match[1]), minute = Math.round(Number(match[2]) / 15) * 15;
        if (minute === 60) { hour += 1; minute = 0; }
        if (hour < this.FIRST_HOUR) { hour = this.FIRST_HOUR; minute = 0; }
        if (hour >= this.LAST_HOUR) { hour = this.LAST_HOUR; minute = 0; }
        const value = `${this.pad(hour)}:${this.pad(minute)}`;
        if (value !== input.value) input.value = value;
    },

    open(input) {
        this.close();
        this.input = input;
        const [hour, minute] = (input.value || '').split(':');
        const panel = document.createElement('div');
        panel.className = 'qtp-panel';
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-label', 'Elegir hora');
        const column = (items, selected, part) => {
            const col = document.createElement('div');
            col.className = 'qtp-column';
            items.forEach(value => {
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'qtp-option' + (value === selected ? ' is-selected' : '');
                button.textContent = value;
                button.setAttribute('aria-pressed', String(value === selected));
                button.addEventListener('click', () => this.pick(part, value));
                col.appendChild(button);
            });
            return col;
        };
        const hours = [];
        for (let h = this.FIRST_HOUR; h <= this.LAST_HOUR; h++) hours.push(this.pad(h));
        panel.append(
            column(hours, hour, 'hour'),
            column(this.MINUTES, this.MINUTES.includes(minute) ? minute : null, 'minute')
        );
        document.body.appendChild(panel);
        this.panel = panel;
        this.limitMinutes(hour);
        this.position();
        // Open at the chosen hour or, for an empty field, at the current hour.
        const hourColumn = panel.querySelector('.qtp-column');
        const now = this.pad(Math.min(this.LAST_HOUR, Math.max(this.FIRST_HOUR, new Date().getHours())));
        const target = hourColumn.querySelector('.is-selected') || [...hourColumn.children].find(button => button.textContent === now);
        if (!hourColumn.querySelector('.is-selected')) target.classList.add('is-now');
        hourColumn.scrollTop = target.offsetTop - hourColumn.offsetTop - 4;
    },

    // At 21 only 21:00 is allowed.
    limitMinutes(hour) {
        this.panel.querySelectorAll('.qtp-column')[1].querySelectorAll('.qtp-option').forEach(button => {
            button.hidden = Number(hour) === this.LAST_HOUR && button.textContent !== '00';
        });
    },

    position() {
        const rect = this.input.getBoundingClientRect();
        const height = this.panel.offsetHeight;
        const below = rect.bottom + 4 + height <= window.innerHeight;
        this.panel.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - this.panel.offsetWidth - 8))}px`;
        this.panel.style.top = `${below ? rect.bottom + 4 : Math.max(8, rect.top - height - 4)}px`;
    },

    pick(part, value) {
        const input = this.input;
        const [hour = '', minute = ''] = (input.value || '').split(':');
        const fallbackHour = this.pad(Math.min(this.LAST_HOUR, Math.max(this.FIRST_HOUR, new Date().getHours())));
        let next = part === 'hour' ? `${value}:${this.MINUTES.includes(minute) ? minute : '00'}` : `${hour || fallbackHour}:${value}`;
        if (Number(next.slice(0, 2)) === this.LAST_HOUR) next = `${this.LAST_HOUR}:00`;
        input.value = next;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        if (part === 'minute') { this.close(); input.focus(); return; }
        this.limitMinutes(next.slice(0, 2));
        this.panel.querySelector('.is-now')?.classList.remove('is-now');
        this.panel.querySelectorAll('.qtp-column').forEach((col, index) => {
            const current = next.split(':')[index];
            col.querySelectorAll('.qtp-option').forEach(button => {
                const selected = button.textContent === current;
                button.classList.toggle('is-selected', selected);
                button.setAttribute('aria-pressed', String(selected));
            });
        });
    },

    close() {
        this.panel?.remove();
        this.panel = null;
        this.input = null;
    }
};

window.QuarterTimePicker = QuarterTimePicker;
QuarterTimePicker.init();
